import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications
} from "sprint-common-web";

import { LanguagesDataContext } from "../../../../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { useCloudAssetDetails } from "../../../../../common/hooks/useCloudAssetDetails";
import { useEditCloudSubtitleAssetLanguage } from "./useEditCloudSubtitleAssetLanguage";

export interface VideoSubtitleFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    subtitleId: string;
}

interface VideoSubtitleFormData {
    subtitleId: string;
}

export const CloudSubtitleAssetLanguageForm: React.FC<VideoSubtitleFormProps> = ({
    onDismiss,
    onSubmit,
    subtitleId
}: VideoSubtitleFormProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { subtitleLanguages } = useContext(LanguagesDataContext);
    const { success, error } = useNotifications();
    const { data: assetDetails } = useCloudAssetDetails();
    const { validateCloudSubtitleAssetLanguage, updateCloudSubtitleAssetLanguage } = useEditCloudSubtitleAssetLanguage({
        invalidationKey: assetDetails?.assetId
    });

    const { handleSubmit, errors, control, formState } = useForm<VideoSubtitleFormData>({
        defaultValues: {
            subtitleId
        }
    });

    const handleSubtitleUpdate = async ({ subtitleId: subtitleLanguageId }: VideoSubtitleFormData) => {
        setIsLoading(true);

        try {
            const result = await validateAndSetResult(subtitleLanguageId);

            if (!result?.isValid) {
                setIsLoading(false);
                return;
            }

            if (!assetDetails) {
                return;
            }

            const updateResponse = await updateCloudSubtitleAssetLanguage.mutateAsync({
                assetId: assetDetails.assetId,
                subtitleLanguageId
            });

            onSubmit(updateResponse.newAssetId);

            success("Subtitle language updated successfully.");
        } catch (e) {
            error("Failed to update subtitle language.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (subtitleLanguageId: string) => {
        if (!assetDetails) {
            return;
        }

        const result = await validateCloudSubtitleAssetLanguage.mutateAsync({
            assetId: assetDetails.assetId,
            subtitleLanguageId
        });

        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleSubtitleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={subtitleLanguages.map((dub) => mapToDropdownOption(dub))}
                    label="Subtitle"
                    name="subtitleId"
                    control={control}
                    errors={errors}
                    disableClear={true}
                    disabled={!subtitleLanguages?.length}
                />
                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
